import type ComponentTypes from "~/features/cms/componentTypes";
import classNames from "classnames";
import parse, { Element, type HTMLReactParserOptions } from "html-react-parser";
import type { CMSProps } from "~/features/cms/types/props";
import { useEffect } from "react";
import { styled } from "~/common/mui";

export interface CMSParagraphProps extends CMSProps {
  rootStyles?: string;
  type: ComponentTypes.CMSParagraph;
}

export const CMSParagraph = styled(
  ({ content = "", className }: CMSParagraphProps) => {
    return (
      <div
        className={classNames(
          "scha__cmssection__html scha__cmssection__sub--paragraph copy_left jsp__user-generated-content",
          className,
        )}
      >
        {parse(content, options)}
      </div>
    );
  },
  { shouldForwardProp: (prop) => prop !== "rootStyles" },
)(({ rootStyles }) => rootStyles);

// 1. Define loose props to accept any attributes passed from the HTML string
interface ScriptExecuterProps {
  children?: string | null;
  [key: string]: unknown; // Allow dynamic attributes (src, async, integrity, etc.)
}

// 2. Helper Component to execute the script
const ScriptExecuter = ({ children, ...props }: ScriptExecuterProps) => {
  useEffect(() => {
    const script = document.createElement("script");

    // Copy all attributes (src, id, async, etc.)
    Object.keys(props).forEach((key) => {
      script.setAttribute(key, String(props[key]));
    });

    // If it has inline code (not a src), set the text
    if (children) {
      script.text = children;
    }

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []); // Run once on mount

  return null; // Render nothing in the UI
};

const options: HTMLReactParserOptions = {
  replace: (domNode) => {
    // 1. Check if the node is a script tag
    if (domNode instanceof Element && domNode.name === "script") {
      const props = domNode.attribs || {};
      const firstChild = domNode.children?.[0];
      const content = firstChild && "data" in firstChild ? firstChild.data : null;

      // 2. Return our custom component that handles execution
      return <ScriptExecuter {...props}>{content}</ScriptExecuter>;
    }
  },
} as const;
